<template>
  <div class="autocomplete form-control">
    <textarea
      class="autocomplete__input px-2 py-2 text-center no-resize no-print"
      :ref="`input-${line}`"
      @input="callOnInput"
      @keyup.down="selectNext()"
      @keyup.up="selectPrevious()"
      @keyup.esc="selectNone()"
      @focus="callOnFocus"
      @blur="callOnBlur"
    >
    </textarea>

    <div
      class="w-100 h-100 autocomplete__input text-center printElement px-2 py-3"
    >
      <span>{{ textQuestion }}</span>
    </div>

    <ul
      v-if="this.items.length && this.showList"
      class="autocomplete__list no-print"
    >
      <li
        v-for="(item, index) in items"
        :key="index"
        @click="callOnSelect(item)"
        class="autocomplete__list__item"
        :class="{
          'autocomplete__list__item--active': item.id === cell?.question_id,
        }"
      >
        <div class="d-flex justify-content-between gap-2">
          <span class="mr-2">{{ item.question }}</span>
          <div class="d-flex my-auto py-1">
            <i
              class="fas fa-edit mr-1"
              @click.stop="enableEditItem(item)"
              data-toggle="tooltip-edit"
              :title="$t('shared.actions.edit')"
            />
            <i
              class="fas fa-trash"
              @click.stop="deleteQuestion(item.id)"
              data-toggle="tooltip-delete"
              :title="$t('shared.actions.delete')"
            />
          </div>
        </div>
      </li>
    </ul>

    <ul
      v-else-if="this.$refs[`input-${this.line}`]?.value && this.showList"
      class="autocomplete__list"
      @click="addQuestion"
    >
      <li class="autocomplete__list__item text-center">
        {{ $t('cryptogramGenerator.button.addQuestion') }}
      </li>
    </ul>

    <ul
      v-else-if="enableEditing"
      class="autocomplete__list"
      @click="editQuestion"
    >
      <li
        class="autocomplete__list__item text-center"
        @click.stop="cancelEdit()"
      >
        {{ $t('shared.actions.cancel') }}
      </li>
      <li class="autocomplete__list__item text-center">
        {{ $t('cryptogramGenerator.button.editQuestion') }}
      </li>
    </ul>
  </div>
</template>

<script>
import PuzzleService from '_services/rest/modules/puzzle';
import { mapActions } from 'vuex';
import Events from '_utils/events';
import {
  _CRYPTOGRAM_CLEAR_QUESTION_,
  _CRYPTOGRAM_SET_QUESTION_,
} from './CryptogramModel';

export default {
  name: 'questions',
  props: {
    cell: {
      type: Object,
      require: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    parameter: {
      type: [String, Number, Object, Array],
      default: null,
    },
    itemValue: {
      type: String,
      default: null,
    },
    itemShow: {
      type: String,
      default: null,
    },
    state: {
      type: String,
      default: null,
    },
    onBlur: {
      type: Function,
      default: () => {},
    },
    onSelect: {
      type: Function,
      default: () => {},
    },
    completeInput: {
      type: Boolean,
      default: true,
    },
    clearInput: {
      type: Boolean,
      default: false,
    },
    closeModal: {
      type: Boolean,
      default: true,
    },
    onEnter: {
      type: Boolean,
      default: true,
    },
    onFocus: {
      type: Function,
      default: null,
    },
    line: {
      type: String,
    },
  },
  data() {
    return {
      items: [],
      inputFocus: false,
      selectedIndex: -1,
      enableEditing: false,
      questionSelected: null,
    };
  },
  computed: {
    showList() {
      return this.inputFocus && !this.enableEditing;
    },
    textQuestion() {
      if (this.cell.text) {
        return this.questionSelected?.question || this.cell.text;
      }
      return '';
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    getQuestions() {
      PuzzleService.getQuestionsByAnswerId(this.cell?.answer_id).then(response => {
        this.items = response;
      });
    },
    addQuestion() {
      PuzzleService.saveQuestion({
        question: this.$refs[`input-${this.line}`].value,
        answer_id: this.cell?.answer_id,
        size_id: 3,
      }).then(response => {
        this.$emit('update-question', response);
        this.activeAlert({
          message: this.$t('cryptogramGenerator.alert.questionAdded'),
          theme: 'success',
        });
        this.inputFocus = false;
      });
    },
    deleteQuestion(id) {
      PuzzleService.deleteQuestion(id).then(() => {
        this.$refs[`input-${this.line}`].value = '';
        this.activeAlert({
          message: this.$t('cryptogramGenerator.alert.questionDeleted'),
          theme: 'success',
        });
        this.inputFocus = false;
      });
    },
    callOnBlur() {
      this.onBlur(this.$refs[`input-${this.line}`].value, this.parameter);
      setTimeout(() => {
        this.inputFocus = false;
      }, 200);
    },
    callOnInput(event) {
      const input = event.target.value;
      if (input !== '') {
        const filteredTerms = this.items.filter(term =>
          term.question.toLowerCase().includes(input.toLowerCase()));

        this.items = filteredTerms;
      }
      if (input === '' && !this.enableEditing) {
        this.getQuestions();
      }
    },
    callOnFocus() {
      if (!this.enableEditing) {
        this.getQuestions();
        this.inputFocus = true;
      }
    },
    callOnSelect(item) {
      this.$refs[`input-${this.line}`].value = this.itemShow
        ? item[this.itemShow]
        : item;
      this.inputFocus = false;
      this.questionSelected = item.question;
      this.$emit('update-question', item);
      if (this.clearInput) {
        this.$refs[`input-${this.line}`].value = '';
      }

      if (this.closeModal) {
        this.close();
      }
    },
    clear() {
      this.$refs[`input-${this.line}`].value = '';
      this.close();
    },
    close() {
      this.items.splice(0, this.items.length);
      this.$set(this, 'selectedIndex', -1);
    },
    selectNext() {
      if (this.selectedIndex < this.items.length - 1) {
        this.selectedIndex += 1;
      }
    },
    selectPrevious() {
      if (this.selectedIndex > 0) {
        this.selectedIndex -= 1;
      }
    },
    selectNone() {
      if (this.selectedIndex > -1) {
        this.close();
      }
    },
    enableEditItem(item) {
      this.inputFocus = true;
      this.$refs[`input-${this.line}`].value = item.question;
      this.enableEditing = true;
      this.questionSelected = item;
    },
    cancelEdit() {
      this.enableEditing = false;
      this.$refs[`input-${this.line}`].value = this.questionSelected.question;
    },
    editQuestion() {
      PuzzleService.editQuestion({
        question: this.$refs[`input-${this.line}`].value,
        answer_id: this.questionSelected.answer_id,
        id: this.questionSelected.id,
        size_id: 3,
      }).then(response => {
        this.$emit('update-question', response);
        this.activeAlert({
          message: this.$t('cryptogramGenerator.alert.questionEdited'),
          theme: 'success',
        });
        this.enableEditing = false;
        this.inputFocus = false;
      });
    },
  },
  mounted() {
    this.dataLocal = [...this.items];
    this.$refs[`input-${this.line}`].value = this.cell.text;

    Events.$on(_CRYPTOGRAM_SET_QUESTION_, answer => {
      if (answer.question && this.line === answer.line.toString()) {
        this.$refs[`input-${this.line}`].value = answer.question.question || '';
        // eslint-disable-next-line prefer-destructuring
        this.questionSelected = answer.question;
      }
    });
    Events.$on(_CRYPTOGRAM_CLEAR_QUESTION_, line => {
      if (this.line && this.line === line.toString()) {
        this.$refs[`input-${this.line}`].value = '';
        this.questionSelected = null;
        this.enableEditing = false;
      }
    });
  },
  destroyed() {
    Events.$off(_CRYPTOGRAM_SET_QUESTION_);
    Events.$off(_CRYPTOGRAM_CLEAR_QUESTION_);
  },
};
</script>

<style lang="scss" scoped>
@import '~_scss/overrides';

.printElement {
  display: none;
  color: black;
}

.no-resize {
  resize: none;
}

.autocomplete__input {
  border: 0;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  width: 100%;
  height: 100%;
  line-height: 1;

  &:focus {
    box-shadow: none;
    border: 0;
  }
}

.autocomplete {
  position: relative;
  border: 0;
  box-shadow: none;
  padding: 0;
  &__list {
    width: 100%;
    padding: 0;

    position: absolute;
    z-index: 10;

    box-shadow: 2px 2px 2px lighten($primary, 25%);
    border: 1px solid $primary;
    border-radius: 5px;
    list-style: none;

    background-color: #fff;

    &__item {
      padding: 5px 15px;

      cursor: pointer;

      &:hover {
        background-color: lighten($primary, 15%);
        color: #fff;
      }

      &:not(:last-child) {
        border-bottom: 1px solid #ccc;
      }

      &--active {
        background-color: $primary;
        color: #fff;
      }
    }
  }
}
</style>
